import { selectBadgeCount } from '@alternativamais/chat-core';
import { useEffect, useRef } from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router';

import { gravarPreferencia } from '../../armazenamento/local';
import { abrirCamada, Camadas, fecharTodasAsCamadas } from '../../chat/camadas';
import { useCliente, useEstado } from '../../chat/contexto';
import {
  BotaoGaveta,
  fecharGaveta,
  useGaveta,
  useJanelaEstreita,
} from '../../chat/gaveta';
import { Icone } from '../../componentes/Icone';
import { useNotificacoesDoNavegador } from '../../chat/notificacoes';
import { DesktopNoChat } from '../../desktop/useDesktop';
import { AvisoConexao } from './AvisoConexao';
import { Canal } from './Canal';
import { Pessoas } from './dms/Pessoas';
import { LateralDms, LateralServidor } from './Lateral';
import { Rail } from './Rail';
import { canalInicial, lembrarCanal } from './rotas';
import { ThreadsQueSigo } from './threads/ThreadsQueSigo';
import { useAtalhosGlobais } from './useAtalhosGlobais';
import { useAusenteAutomatico } from './useAusenteAutomatico';

/**
 * A casca (Rail + Lateral + conteúdo) e as rotas do Discord:
 * `/canais/@me` (Pessoas), `/canais/@me/threads` (Threads que sigo),
 * `/canais/@me/:canal`, `/canais/:servidor/:canal[/:mensagem]` e a thread
 * aberta ao lado, `/canais/:servidor/:canal/threads/:thread`.
 */
export function Aplicativo() {
  const cliente = useCliente();
  const { servidor, canal, mensagem, thread } = useParams();
  const local = useLocation();
  const navegar = useNavigate();
  const [consulta] = useSearchParams();
  useNotificacoesDoNavegador();
  const dms = !servidor || servidor === '@me';
  useAtalhosGlobais(
    dms ? null : (servidor ?? null),
    canal && canal !== 'threads' ? (thread ?? canal) : null,
  );
  useAusenteAutomatico();
  const guildId = dms ? null : servidor;
  const existe = useEstado((estado) =>
    guildId ? !!estado.guilds[guildId] : true,
  );
  const tituloCanal = useEstado((estado) =>
    canal ? (estado.channels[canal]?.name ?? null) : null,
  );
  const nomeServidor = useEstado((estado) =>
    guildId ? (estado.guilds[guildId]?.name ?? null) : null,
  );
  const badge = useEstado(selectBadgeCount);

  useEffect(() => {
    gravarPreferencia('ultima-rota', local.pathname);
  }, [local.pathname]);
  useEffect(() => {
    if (guildId && canal) lembrarCanal(guildId, canal);
  }, [guildId, canal]);
  // Trocar de canal fecha perfil, visualizador e modais abertos.
  useEffect(() => fecharTodasAsCamadas(servidor), [servidor, canal]);
  // Janela estreita: a gaveta fecha ao abrir um canal (trocar de servidor
  // na barra não fecha, para escolher o canal dele).
  const estreita = useJanelaEstreita();
  const gavetaAberta = useGaveta((estado) => estado.aberta) && estreita;
  const anterior = useRef({ servidor, canal });
  useEffect(() => {
    const antes = anterior.current;
    if (antes.servidor === servidor && antes.canal !== canal) fecharGaveta();
    anterior.current = { servidor, canal };
  }, [servidor, canal]);
  useEffect(() => {
    if (!gavetaAberta) return;
    const tecla = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') fecharGaveta();
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [gavetaAberta]);
  // Link de evento (`?evento=`): abre o detalhe.
  const eventoDoLink = consulta.get('evento');
  const conviteDoLink = consulta.get('convite');
  useEffect(() => {
    if (!conviteDoLink) return;
    abrirCamada({ tipo: 'entrar-servidor', codigo: conviteDoLink });
    navegar(local.pathname, { replace: true });
  }, [conviteDoLink, local.pathname, navegar]);
  useEffect(() => {
    if (!guildId || !eventoDoLink) return;
    abrirCamada({ tipo: 'evento', guildId, eventoId: eventoDoLink });
    navegar(local.pathname, { replace: true });
  }, [guildId, eventoDoLink, local.pathname, navegar]);
  useEffect(() => {
    const partes = [
      tituloCanal ? `#${tituloCanal}` : null,
      nomeServidor,
      'Alternativa Chat',
    ].filter(Boolean);
    document.title = `${badge ? `(${badge}) ` : ''}${partes.join(' | ')}`;
  }, [tituloCanal, nomeServidor, badge]);

  if (guildId && !existe) return <Navigate to="/canais/@me" replace />;
  if (guildId && !canal) {
    const inicial = canalInicial(cliente.store.getState(), guildId);
    if (inicial)
      return <Navigate to={`/canais/${guildId}/${inicial}`} replace />;
  }

  return (
    <div className="flex h-full flex-col">
      <AvisoConexao />
      <div className="flex min-h-0 grow">
        {gavetaAberta && (
          <button
            type="button"
            aria-label="Fechar canais e servidores"
            onClick={fecharGaveta}
            className="fixed inset-0 z-30 bg-black/50"
          />
        )}
        <div
          inert={estreita && !gavetaAberta}
          className={
            estreita
              ? `fixed inset-y-0 left-0 z-30 flex max-w-[calc(100vw-48px)] transition-transform duration-200 ${
                  gavetaAberta
                    ? 'shadow-[0_12px_32px_rgba(0,0,0,.45)]'
                    : '-translate-x-full'
                }`
              : 'contents'
          }
        >
          <Rail servidorAtual={guildId} />
          {guildId ? (
            <LateralServidor guildId={guildId} canalAtual={canal ?? null} />
          ) : (
            <LateralDms canalAtual={canal ?? null} />
          )}
        </div>
        {dms && canal === 'threads' ? (
          <ThreadsQueSigo />
        ) : canal ? (
          <Canal
            key={canal}
            canalId={canal}
            mensagemInicial={mensagem}
            threadId={thread}
          />
        ) : dms ? (
          <Pessoas />
        ) : (
          <InicioVazio />
        )}
      </div>
      <Camadas />
      <DesktopNoChat />
    </div>
  );
}

function InicioVazio() {
  return (
    <div className="relative flex min-w-0 grow flex-col items-center justify-center gap-3 bg-chat p-8 text-center">
      <span className="absolute top-2 left-4">
        <BotaoGaveta />
      </span>
      <span className="flex size-16 items-center justify-center rounded-[18px] bg-elevado text-destaque">
        <Icone nome="hash" tamanho={36} />
      </span>
      <h2 className="m-0 font-titulo text-2xl font-bold">
        Nenhum canal para abrir
      </h2>
      <p className="m-0 max-w-[420px] text-[15px] text-texto-2">
        Você ainda não vê nenhum canal de texto neste servidor.
      </p>
    </div>
  );
}
